import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import RTLBannerImage from "../../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../../assets/services/asic-design/asicDesign1.png";
import "./FrontEndRTLDesign.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? <mark key={i}>{part}</mark> : part
  );
};

const FrontEndRTLDesign = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);
  const closeOverlay = () => setShowThankYou(false);

  const [replyVisible, setReplyVisible] = useState({});
  const toggleReply = (id) =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scrolling
  const sectionRefs = {
    intro: useRef(null),
    concepts: useRef(null),
    guidelines: useRef(null),
    architecture: useRef(null),
    interfaces: useRef(null),
    parameterization: useRef(null),
    testbench: useRef(null),
    synthesis: useRef(null),
    debugging: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSearch = () => setSearchTerm(searchInput.trim());
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  useEffect(() => {
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (showThankYou) {
      const t = setTimeout(() => setShowThankYou(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showThankYou]);

  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowThankYou(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page front-end-rtl-design">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Front-End RTL Design</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-design">Services</Link> | Front-End RTL Design
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            Search
          </button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    concepts: "Key Concepts",
                    guidelines: "Coding Guidelines",
                    architecture: "Module Architecture",
                    interfaces: "Interfaces & Buses",
                    parameterization: "Parameterization",
                    testbench: "Testbench Integration",
                    synthesis: "Synthesis Considerations",
                    debugging: "Debugging & Linting",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="blog-content-container">
        {/* Left Column */}
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={RTLBannerImage}
            alt="RTL Design"
          />

          <div className="post-info-row fade-section">
            <span className="post-author">By Jane Smith</span>
            <span className="post-category">Category: Tech</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              2 Comments
            </button>
          </div>

          <h2 ref={sectionRefs.intro} className="fade-section">
            Introduction to Front-End RTL Design
          </h2>
          <p className="fade-section">
            <Highlight
              text="Front-End RTL Design is the craft of writing synthesizable hardware description code—usually in SystemVerilog or VHDL—that defines the behavior of digital blocks before synthesis and place-and-route."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="This stage bridges architecture and implementation: you translate block diagrams and state-machine definitions into clean, modular RTL with clear interfaces and parameters."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Effective RTL design reduces iteration cycles downstream: clean code helps static checkers, synthesis tools, and timing analysis converge faster, saving weeks of back-and-forth."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Early planning of pipeline depths, handshake protocols, and clock domains sets the foundation for robust, scalable designs that integrate smoothly into larger SoC flows."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.concepts} className="fade-section">
            Key Concepts
          </h2>
          <ul className="fade-section">
            {[
              "Clocking and Reset Strategies",
              "Finite-State Machines (FSMs)",
              "Pipeline Stages & Handshakes",
              "Parameterizable Modules",
            ].map(step => (
              <li key={step}>
                <Highlight text={step} query={searchTerm} />
              </li>
            ))}
          </ul>
          <p className="fade-section">
            <Highlight
              text="Understanding timing domains and handshake protocols is vital to avoid metastability and ensure reliable data flow across blocks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Defining clear stable and async resets ensures predictable bring-up behavior, especially in multi-clock setups."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Explicit FSM encodings (one-hot vs binary) trade off between area and timing—one-hot simplifies synthesis, binary reduces register count."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.guidelines} className="fade-section">
            Coding Guidelines
          </h2>
          <p className="fade-section">
            <Highlight
              text="Adhering to style guides—clear naming, consistent indentation, avoidance of inferrable latches—improves readability and eases formal checks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use explicit signal widths, avoid ‘default’ statements inside case constructs, and prefer blocking assignments for combinational logic."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Group related signals into interfaces or structs to maintain code locality and simplify port lists at higher hierarchy levels."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.architecture} className="fade-section">
            Module Architecture
          </h2>
          <p className="fade-section">
            <Highlight
              text="Top-down module partitioning, clean input/output definitions, and use of interfaces/packages enable scalable RTL hierarchies."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Separate control and datapath logic into distinct submodules—this separation enhances testability and formal proof efforts."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Encapsulate internal registers and signals, exposing only necessary ports; this minimizes coupling and eases reuse in other projects."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.interfaces} className="fade-section">
            Interfaces & Buses
          </h2>
          <p className="fade-section">
            <Highlight
              text="Standard protocols (AXI, AHB, APB) and custom handshake interfaces should be wrapped in SystemVerilog interfaces to enforce signal grouping and timing!"
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Using SV interfaces reduces manual port errors, auto-bundles signals in simulation waveforms, and simplifies connecting transactors in UVM testbenches."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Parameterize bus widths and address offsets via interface parameters to create a single reusable interface definition across different IP blocks."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.parameterization} className="fade-section">
            Parameterization
          </h2>
          <p className="fade-section">
            <Highlight
              text="Leveraging parameters and generate blocks makes your RTL flexible—able to target multiple data-widths or feature-sets from one codebase."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Define clear parameter defaults and enforce valid ranges with assertions to catch misconfigurations at elaboration time."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use generate loops for replicated structures (FIFOs, register arrays), enabling easy scaling without manual code duplication."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.testbench} className="fade-section">
            Testbench Integration
          </h2>
          <p className="fade-section">
            <Highlight
              text="Integrate with UVM or directed testbenches early—drive your RTL with constraint-random vectors, self-checking scoreboards, and coverage goals."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Wrap your DUT in an SV interface adapter to eliminate manual signal toggles and simplify scoreboard connections."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Automate register model generation from RTL descriptions or IP-XACT to ensure consistency between design and verification layers."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.synthesis} className="fade-section">
            Synthesis Considerations
          </h2>
          <p className="fade-section">
            <Highlight
              text="Avoid overly complex expressions, use pipeline registers where needed, and annotate false/clock-gating paths via your SDC for accurate results."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Check synthesis directives (synopsys translate_off/on) to exclude simulation-only constructs from netlist."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Run lint and synthesis reports side by side to catch mismatches in inferred memory structures or unsupported language constructs."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.debugging} className="fade-section">
            Debugging & Linting
          </h2>
          <p className="fade-section">
            <Highlight
              text="Tools like SpyGlass, Verilator lint, and in-simulation waveform analysis catch common pitfalls early—stale signals, width mismatches, and unintended inferrable latches."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Set up automated lint runs in your CI pipeline to enforce style and coding rules before code merges."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use signal dump macros and VCD comparison between golden and regression runs to pinpoint functional discrepancies quickly."
              query={searchTerm}
            />
          </p>

          <h2 ref={sectionRefs.trends} className="fade-section">
            Future Trends
          </h2>
          <p className="fade-section">
            <Highlight
              text="High-level synthesis (HLS), AI-assisted RTL generation, and real-time code analysis are on the horizon to accelerate front-end flows."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Integration of formal property generation and coverage-driven verification at the code generation level promises near-zero-defect RTL deliveries."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Cloud-based, collaborative RTL IDEs with live lint and synthesis feedback will redefine how teams develop and review code remotely."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Alex Developer</span>
                    <span className="comment-date">April 20, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great deep dive on RTL coding standards—very helpful!
              </p>
              <button className="reply-button" onClick={() => toggleReply("alex")}>
                Reply
              </button>
              <AnimatePresence initial={false}>
                {replyVisible["alex"] && (
                  <motion.div
                    className="leave-comment"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>Reply to Alex Developer</h3>
                    <form onSubmit={handleCommentSubmit}>
                      <div className="form-group">
                        <label>Name</label>
                        <input type="text" placeholder="Your name..." required />
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea placeholder="Your reply..." required />
                      </div>
                      <button type="submit" className="send-message-btn">
                        Reply
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Jamie Tester</span>
                    <span className="comment-date">April 21, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Loved the examples on parameterization—makes so much sense now!
              </p>
              <button className="reply-button" onClick={() => toggleReply("jamie")}>
                Reply
              </button>
              <AnimatePresence initial={false}>
                {replyVisible["jamie"] && (
                  <motion.div
                    className="leave-comment"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>Reply to Jamie Tester</h3>
                    <form onSubmit={handleCommentSubmit}>
                      <div className="form-group">
                        <label>Name</label>
                        <input type="text" placeholder="Your name..." required />
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea placeholder="Your reply..." required />
                      </div>
                      <button type="submit" className="send-message-btn">
                        Reply
                      </button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      concepts: "Key Concepts",
                      guidelines: "Coding Guidelines",
                      architecture: "Module Architecture",
                      interfaces: "Interfaces & Buses",
                      parameterization: "Parameterization",
                      testbench: "Testbench Integration",
                      synthesis: "Synthesis Considerations",
                      debugging: "Debugging & Linting",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showThankYou && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FrontEndRTLDesign;